import { useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage } from '../services/api';

export interface Quiz {
  id: number;
  topicId: number;
  userId: number;
  title: string;
  slug: string;
  quizType: 'memory-game' | 'flashcards' | 'image-pairing' | 'image-sequencing';
  backgroundImagePath: string | null;
  settingsJson: string;
  isPublished: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useQuizzes(topicId?: number) {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuizzes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/quizzes');
      const all = res.data.data as Quiz[];
      setQuizzes(topicId ? all.filter((q) => q.topicId === topicId) : all);
      setError(null);
    } catch (err: unknown) {
      // Without this the failed request renders as "пока нет квизов".
      setError(getApiErrorMessage(err, 'Не удалось загрузить список квизов'));
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  const createQuiz = async (data: {
    topicId: number;
    title: string;
    quizType: Quiz['quizType'];
    wordIds?: number[];
    settings?: Record<string, unknown>;
  }) => {
    const res = await api.post('/quizzes', data);
    setQuizzes((prev) => [...prev, res.data.data]);
    return res.data.data as Quiz;
  };

  const updateQuiz = async (id: number, data: Partial<Quiz>) => {
    const res = await api.patch(`/quizzes/${id}`, data);
    setQuizzes((prev) => prev.map((q) => (q.id === id ? res.data.data : q)));
    return res.data.data as Quiz;
  };

  const deleteQuiz = async (id: number) => {
    await api.delete(`/quizzes/${id}`);
    setQuizzes((prev) => prev.filter((q) => q.id !== id));
  };

  return { quizzes, loading, error, fetchQuizzes, createQuiz, updateQuiz, deleteQuiz };
}

export function useQuiz(id: number | undefined) {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuiz = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/quizzes/${id}`);
      setQuiz(res.data.data);
      setError(null);
    } catch (err: unknown) {
      // Ownership guards answer 404, so this also covers "not your quiz".
      setError(getApiErrorMessage(err, 'Не удалось загрузить квиз'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  return { quiz, loading, error, fetchQuiz };
}